import { WaterQualityData, WaterQualityScore, CropType, CropCompatibility } from '../types/waterQuality';

/**
 * Calculate the water quality score based on various parameters
 */
export const calculateWaterQualityScore = (waterData: WaterQualityData): WaterQualityScore => {
  // Initialize scores
  let phScore = 100;
  let ecScore = 100;
  let mineralBalance = 100;
  let toxicityRisk = 100;
  
  // pH score calculation
  if (waterData.ph < 5.5) {
    phScore = Math.max(0, 100 - (5.5 - waterData.ph) * 40);
  } else if (waterData.ph > 6.5) {
    phScore = Math.max(0, 100 - (waterData.ph - 6.5) * 30);
  }
  
  // EC score calculation
  if (waterData.ec > 1.0) {
    ecScore = Math.max(0, 100 - (waterData.ec - 1.0) * 25);
  }
  
  // Mineral balance calculation
  const caToMgRatio = waterData.ca / (waterData.mg || 1); // Avoid division by zero
  if (caToMgRatio < 3 || caToMgRatio > 6) {
    mineralBalance -= 20;
  }
  
  if (waterData.k > 10) {
    mineralBalance -= 10;
  }
  
  // Toxicity risk calculation
  if (waterData.na > 50) {
    toxicityRisk -= 30;
  }
  
  if (waterData.cl > 70) {
    toxicityRisk -= 30;
  }
  
  // Ensure scores are between 0 and 100
  phScore = Math.max(0, Math.min(100, phScore));
  ecScore = Math.max(0, Math.min(100, ecScore));
  mineralBalance = Math.max(0, Math.min(100, mineralBalance));
  toxicityRisk = Math.max(0, Math.min(100, toxicityRisk));
  
  // Calculate overall score (weighted average)
  const overall = Math.round(
    (phScore * 0.3) + 
    (ecScore * 0.2) + 
    (mineralBalance * 0.3) + 
    (toxicityRisk * 0.2)
  );
  
  return {
    overall,
    phScore,
    ecScore,
    mineralBalance,
    toxicityRisk
  };
};

/**
 * Calculate crop compatibility score based on water quality data
 */
export const calculateCropScore = (waterData: WaterQualityData, cropType: CropType): number => {
  let score = 100;
  
  switch (cropType) {
    case 'leafy':
      // Leafy greens are sensitive to high EC and sodium
      if (waterData.ec > 1.0) score -= (waterData.ec - 1.0) * 25;
      if (waterData.na > 30) score -= (waterData.na - 30) * 0.5;
      if (waterData.ph < 5.5 || waterData.ph > 6.5) score -= 20;
      break;
      
    case 'fruiting':
      // Fruiting plants often need more calcium and can handle higher EC
      if (waterData.ec > 1.5) score -= (waterData.ec - 1.5) * 20;
      if (waterData.ca < 40) score -= (40 - waterData.ca) * 0.5;
      if (waterData.ph < 5.8 || waterData.ph > 6.8) score -= 15;
      break;
      
    case 'root':
      // Root vegetables are more sensitive to high mineral content
      if (waterData.ec > 0.8) score -= (waterData.ec - 0.8) * 30;
      if (waterData.cl > 40) score -= (waterData.cl - 40) * 0.5;
      if (waterData.ph < 6.0 || waterData.ph > 7.0) score -= 15;
      break;
      
    case 'herbs':
      // Herbs often prefer less nutrient-dense water
      if (waterData.ec > 1.2) score -= (waterData.ec - 1.2) * 20;
      if (waterData.na > 25) score -= (waterData.na - 25) * 0.6;
      if (waterData.ph < 5.5 || waterData.ph > 6.5) score -= 15;
      break;
      
    case 'berries':
      // Berries often prefer more acidic water
      if (waterData.ph > 6.0) score -= (waterData.ph - 6.0) * 15;
      if (waterData.ec > 1.0) score -= (waterData.ec - 1.0) * 25;
      break;
  }
  
  // Ensure score is between 0 and 100
  return Math.max(0, Math.min(100, Math.round(score)));
};

/**
 * Get recommendations based on water quality and crop type
 */
export const getCropRecommendations = (
  waterData: WaterQualityData, 
  cropType: CropType
): string[] => {
  const recommendations: string[] = [];
  
  switch (cropType) {
    case 'leafy':
      if (waterData.ec > 1.0) {
        recommendations.push('Dilute your water to reduce EC for better leafy green growth');
      }
      if (waterData.na > 30) {
        recommendations.push('Consider using reverse osmosis to reduce sodium levels');
      }
      break;
      
    case 'fruiting':
      if (waterData.ca < 40) {
        recommendations.push('Add calcium to your nutrient solution for better fruit development');
      }
      if (waterData.ph < 5.8) {
        recommendations.push('Raise pH slightly for optimal nutrient uptake in fruiting plants');
      }
      break;
      
    // Add more crop-specific recommendations
  }
  
  // General recommendations
  if (waterData.ph < 5.5) {
    recommendations.push('Increase pH with potassium hydroxide or potassium carbonate');
  } else if (waterData.ph > 7.0) {
    recommendations.push('Lower pH with phosphoric or citric acid');
  }
  
  if (waterData.ec > 1.5) {
    recommendations.push('Consider diluting your water with reverse osmosis water');
  }
  
  return recommendations;
};

/**
 * Get crop compatibility for multiple crop types
 */
export const getCropCompatibility = (waterData: WaterQualityData): CropCompatibility[] => {
  const cropTypes: CropType[] = ['leafy', 'fruiting', 'root', 'herbs', 'berries'];
  
  return cropTypes.map(cropType => ({
    name: getCropTypeName(cropType),
    score: calculateCropScore(waterData, cropType),
    recommendations: getCropRecommendations(waterData, cropType)
  }));
};

/**
 * Get display name for crop type
 */
const getCropTypeName = (cropType: CropType): string => {
  switch (cropType) {
    case 'leafy': return 'Leafy Greens';
    case 'fruiting': return 'Fruiting Plants';
    case 'root': return 'Root Vegetables';
    case 'herbs': return 'Herbs';
    case 'berries': return 'Berries';
    default: return cropType;
  }
};
